/**
 * InventorySidebar
 *
 * Flat (no-accordion) navigation for the Inventory module.
 * Every link is a top-level item with identical size, font and styling.
 *
 * Order (top → bottom):
 *   הפקת מסמך חדש      /app/inventory/documents/new
 *   ניהול הזמנות        /app/inventory/movements
 *   ניהול קונסגנציה     /app/inventory/consignment    (placeholder)
 *   מלאי ומוצרים        /app/inventory/stock
 *   הגדרות מודל ERP    /app/inventory/settings   (editor+)
 *   ─── divider ───
 *   הגדרות מערכת       /app/settings
 *   ניהול מערכת         /app/admin                (super-admin only)
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, LogOut, Package, FileText, Box, Boxes, Shield, FilePlus as FilePlus2, Download, Brain, ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useMobileMenu } from '@/context/MobileMenuContext';

// ─── Flat link definition ─────────────────────────────────────────────────────

interface FlatLink {
  nameKey:           string;
  path:              string;
  icon:              React.ReactNode;
  requiresEditor?:   boolean;   // inventory editor permission
  requiresSuperAdmin?: boolean;
  dividerBefore?:    boolean;   // render a thin divider above this item
}

const FLAT_LINKS: FlatLink[] = [
  {
    nameKey: 'nav.newDocument',
    path:    '/app/inventory/documents/new',
    icon:    <FilePlus2 className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.inventoryMovements',          // renamed → "ניהול הזמנות"
    path:    '/app/inventory/movements',
    icon:    <FileText className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.inventoryConsignment',        // NEW → "ניהול קונסגנציה" (placeholder)
    path:    '/app/inventory/consignment',
    icon:    <Boxes className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.inventoryStock',              // renamed → "מלאי ומוצרים"
    path:    '/app/inventory/stock',
    icon:    <Box className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.inventoryImport',
    path:    '/app/inventory/import',
    icon:    <Download className="w-5 h-5" />,
  },
  {
    nameKey: 'nav.stockCount',
    path:    '/app/inventory/stock-count',
    icon:    <ClipboardList className="w-5 h-5" />,
  },
  {
    nameKey:       'nav.inventorySettings',
    path:          '/app/inventory/settings',
    icon:          <Settings className="w-5 h-5" />,
    requiresEditor: true,
  },
  // ── general / admin ──────────────────────────────────────────
  {
    nameKey:       'nav.aiReports',
    path:          '/app/reports/ai',
    icon:          <Brain className="w-5 h-5" />,
    dividerBefore: true,
  },
  {
    nameKey:       'nav.settings',
    path:          '/app/settings',
    icon:          <Settings className="w-5 h-5" />,
  },
  {
    nameKey:             'nav.admin',
    path:                '/app/admin',
    icon:                <Shield className="w-5 h-5" />,
    requiresSuperAdmin:  true,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function InventorySidebar() {
  const location                              = useLocation();
  const navigate                              = useNavigate();
  const { signOut, user }                     = useAuth();
  const { t }                                 = useTranslation();
  const { activeCompanyRole }                 = useCompany();
  const { can, isPremium, isSuperAdmin }      = usePermissions();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [isLoggingOut, setIsLoggingOut]       = useState(false);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const isActive = (path: string): boolean =>
    location.pathname === path.split('?')[0];

  const canView = (link: FlatLink): boolean => {
    if (link.requiresSuperAdmin && !isSuperAdmin) return false;
    if (link.requiresEditor && !can({ module: 'inventory', level: 'editor' })) return false;
    return true;
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // ── Shared link class ─────────────────────────────────────────────────────

  const linkCls = (path: string) =>
    [
      'flex items-center gap-3 px-4 py-3 rounded-lg',
      'text-sm font-medium transition-all duration-200',
      isActive(path)
        ? 'bg-primary-50 text-primary-700'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
    ].join(' ');

  // ── Render ────────────────────────────────────────────────────────────────

  const visibleLinks = FLAT_LINKS.filter(canView);

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={[
          'fixed md:static inset-y-0 right-0 z-40 md:z-30',
          'w-64 bg-white border-l md:border-l-0 md:border-r border-gray-200',
          'transform transition-transform duration-300 ease-in-out',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0',
          'flex flex-col',
        ].join(' ')}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-gray-200 px-6">
          <Link to="/app/inventory" className="flex items-center gap-2">
            <Package className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-gray-900">Axelio</span>
          </Link>
        </div>

        {/* Flat navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {visibleLinks.map(link => (
            <div key={link.path}>
              {link.dividerBefore && (
                <hr className="my-2 border-gray-100" />
              )}
              <Link
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={linkCls(link.path)}
              >
                {link.icon}
                <span>{t(link.nameKey)}</span>
              </Link>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          {/* Premium / Super-Admin badge */}
          {(isPremium || isSuperAdmin) && (
            <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 text-center">
                {isSuperAdmin ? '🛡️ Super Admin' : '⭐ Premium'}
              </p>
            </div>
          )}

          {/* Role badge */}
          {activeCompanyRole && (
            <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 text-center capitalize">
                {activeCompanyRole}
              </p>
            </div>
          )}

          {/* Email */}
          {user?.email && (
            <div className="px-3 py-2 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 truncate text-center">
                {user.email}
              </p>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5
                       text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogOut className="w-4 h-4" />
            <span>{isLoggingOut ? 'Logging out...' : t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  );
}
